import { useState, useEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { FiMail, FiActivity, FiClock } from "react-icons/fi";
import InfiniteScroll from "react-infinite-scroll-component";

function DrPatientGrid({ getOutPatients, isLoading, isSuccess, isFetching, selectedFilters }) {
  const navigate = useNavigate();
  const location = useLocation();

  const handlePatientClick = (patientId) => {
    navigate(`/patientsOverview/${patientId}?from=${encodeURIComponent(
              location.pathname + location.search
            )}`);
  };

  const getStatusColor = (condition) => {
    switch (condition?.toLowerCase()) {
      case "critical":
        return "bg-red-100 text-red-800 border-red-200";
      case "recovery":
        return "bg-yellow-100 text-yellow-800 border-yellow-200";
      case "stable":
        return "bg-green-100 text-green-800 border-green-200";
      default:
        return "bg-gray-100 text-gray-800 border-gray-200";
    }
  };

  const getInitialsBg = (index) => {
    const colors = [
      "linear-gradient(135deg, #f472b6, #ec4899)",
      "linear-gradient(135deg, #a855f7, #9333ea)",
      "linear-gradient(135deg, #60a5fa, #3b82f6)",
      "linear-gradient(135deg, #22d3ee, #06b6d4)",
      "linear-gradient(135deg, #34d399, #10b981)",
      "linear-gradient(135deg, #facc15, #eab308)",
      "linear-gradient(135deg, #fb923c, #f97316)",
      "linear-gradient(135deg, #f87171, #ef4444)",
      "linear-gradient(135deg, #818cf8, #6366f1)",
    ];
    return colors[index % colors.length];
  };
    
  const [doctors, setDoctors] = useState([]);
    const [hasMore, setHasMore] = useState(true);
    const [page, setPage] = useState(1);
  
    function convertFilters(selectedFilters) {
      if (!selectedFilters) return [];
      return Object.entries(selectedFilters).map(([key, valueSet]) => ({
        filterName: key,
        filterOperator: valueSet.operator,
        filterValue: Array.from(valueSet.values).join(", "),
      }));
    }
  
    const fetchGridData = async (offset) => {
      try {
        const pageSize = 5;
        const filters = convertFilters(selectedFilters);
        const result = await getOutPatients(offset, filters);
        
  
        if (Array.isArray(result)) {
          setDoctors((prev) => (offset === 1 ? result : [...prev, ...result]));
          setHasMore(result.length === pageSize);
        } else {
          setHasMore(false);
        }
      } catch (err) {
        console.error("Error fetching data:", err);
        setHasMore(false);
      }
    };
  
    const fetchNextData = () => {
      const nextPage = page + 1;
      setPage(nextPage);
    };
  
    useEffect(() => {
      setDoctors([]);
      setPage(1);
      setHasMore(true);
      fetchGridData(1); 
    }, [selectedFilters]);
  
  useEffect(() => {
    if (hasMore) {
      fetchGridData(page);
    }
  }, [page]);
  
    useEffect(() => {
  
  }, [selectedFilters]);
  

  // Conditional render
  if (isSuccess && doctors.length === 0 && !isFetching) {
    return (
      <div className="flex flex-col items-center justify-center h-full ">
        <img
          src="/Images/doctortable.png"
          alt="No doctors available"
          className="w-64 h-64 object-contain mb-6"
        />
        <p className="text-gray-600 text-lg font-semibold">Not Available</p>
      </div>
    );
  }

  return (
       <InfiniteScroll
          dataLength={doctors?.length}
          next={fetchNextData}
          hasMore={hasMore}
          loader={<h4>Loading...</h4>}
          endMessage={
            <p style={{ textAlign: "center" }}>
              <b>Yay! You have seen it all</b>
            </p>
          }
          scrollableTarget="scrollableDiv"
        >
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100 p-6">
      <div className="max-w-9xl mx-auto">
        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-2 lg:grid-cols-3 2xl:grid-cols-4 gap-6">
          {doctors.map((patient, index) => (
            <div
              key={index}
              className="group relative bg-white rounded-2xl shadow-lg hover:shadow-2xl transition-all duration-300 transform hover:-translate-y-2 border border-gray-100 overflow-visible mb-3"
            >
              <div className="absolute top-3 right-3 flex space-x-1">
                <button className="p-1.5 bg-white/20 hover:bg-white/30 rounded-full backdrop-blur-sm transition-all">
                  <svg className="w-3 h-3 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z" />
                  </svg>
                </button>
                <button className="p-1.5 bg-white/20 hover:bg-white/30 rounded-full backdrop-blur-sm transition-all">
                  <svg className="w-3 h-3 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                  </svg>
                </button>
              </div>

              <div className="absolute -top-5 left-4 z-20">
                <div className="bg-white px-3 py-1 rounded-full shadow-md border border-gray-200">
                  <span className="text-xs font-semibold text-gray-700">
                    #{patient?.patientID || `PLCRXPAT${String(index + 1).padStart(3, "0")}`}
                  </span>
                </div>
              </div>

              <div className="p-4 pt-4 relative">
                <div className="flex items-center space-x-3 mb-4">
                  <div
                    className="w-12 h-12 rounded-xl flex items-center justify-center shadow-lg"
                    style={{ background: getInitialsBg(index) }}
                  >
                    <span className="text-white font-bold text-lg">
                      {patient?.patientName?.charAt(0) || "P"}
                    </span>
                  </div>
                  <div className="flex-1 min-w-0">
                    <h3 className="font-semibold text-gray-900 truncate">
                      {patient?.patientName || "Unknown"}
                    </h3>
                    <p className="text-sm text-gray-500 truncate">
                      Appointment #{patient?.appointmentID}
                    </p>
                  </div>
                </div>

                <div className="mb-4">
                  <span
                    className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium border ${getStatusColor(
                      patient?.status
                    )}`}
                  >
                    <div
                      className={`w-1.5 h-1.5 rounded-full mr-1.5 ${
                        patient?.status?.toLowerCase() === "critical"
                          ? "bg-red-500"
                          : patient?.status?.toLowerCase() === "recovery"
                          ? "bg-yellow-500"
                          : "bg-green-500"
                      }`}
                    ></div>
                    {patient?.status}
                  </span>
                </div>

                <div className="space-y-3">
                  <div className="flex items-center space-x-2 text-sm text-gray-600">
                    <div className="w-4 h-4 bg-gradient-to-r from-[#7ceaae] to-[#38b286] rounded-full flex items-center justify-center">
                      <FiActivity />
                    </div>
                    <span className="flex-1 truncate font-medium">
                      {patient?.diagnosis || "N/A"}
                    </span>
                  </div>

                  <div className="flex items-center space-x-2 text-sm text-gray-600">
                    <div className="w-4 h-4 bg-gradient-to-r from-[#7ceaae] to-[#38b286] rounded-full flex items-center justify-center">
                      <FiClock />
                    </div>
                    <span>Last visit: {patient?.dateCheckedIn || "N/A"}</span>
                  </div>

                  <div className="flex items-center space-x-2 text-sm text-gray-600">
                    <div className="w-4 h-4 bg-gradient-to-r from-[#7ceaae] to-[#38b286] rounded-full flex items-center justify-center">
                      <FiMail />
                    </div>
                    <span className="flex-1 truncate">{patient?.patientUserUniqueID}</span>
                  </div>
                </div>

                <div className="mt-3 pt-4 border-t border-gray-100">
                  <button
                    onClick={() => handlePatientClick(patient?.patientUserUniqueID)}
                    className="w-full text-white font-semibold py-2.5 rounded-xl hover:shadow-lg transform hover:scale-105 transition-all duration-200 shadow-md"
                    style={{
                      background:
                        "linear-gradient(135deg, #7ceaae 0%, #53c898 50%, #38b286 100%)",
                    }}
                  >
                    View Details
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
    </InfiniteScroll>
  );
}

export default DrPatientGrid;
